// Builds a Nuxt error from an exception caught while loading page data, telling a
// genuine connectivity failure apart from a real server/HTTP error.
//
// ofetch rejects an offline request with a *statusless* FetchError (there is no HTTP
// response, so `statusCode`/`status` are undefined). Callers used to fall back to
// `error?.statusCode || 500`, which turned every offline failure into a fake 500 — the
// error page then read "Something broke on our end" instead of telling the user they're
// offline. This helper produces a 503 "You're offline" error for those cases and only
// reports a real status when the server actually responded.

const getStatus = (error: any): number | null =>
  error?.statusCode ?? error?.status ?? error?.response?.status ?? null;

// A network-level failure has no HTTP response, so it carries no status. This covers offline,
// DNS failure, connection refused, aborts and timeouts - all "check your connection", never
// "our server is broken".
//
// Exported because the same test decides whether a failed lead scan is queued on device and
// which message parseErrorFromException shows. It deliberately no longer consults
// navigator.onLine: that flag has been seen stuck false while the network was fine (see
// useConnectivity), and a statusless failure is the stronger signal anyway.
export const isConnectivityError = (error: any): boolean =>
  getStatus(error) === null
  && ['FetchError', 'AbortError', 'TimeoutError'].includes(error?.name);

export function createDataLoadError(error: any, fallbackMessage: string) {
  const status = getStatus(error);

  if (isConnectivityError(error)) {
    return createError({
      statusCode: 503,
      statusMessage: 'Check your internet connection and try again.',
      data: { title: "You're offline" },
    });
  }

  // The server answered: report what it said.
  if (status !== null) {
    return createError({
      statusCode: status,
      statusMessage: error?.statusMessage || fallbackMessage,
    });
  }

  // No HTTP response and not a recognized connectivity failure: the request never produced an
  // answer (a throw while handling the response, an unexpected rejection). Nothing on the server
  // failed, so this must not fall back to a 500 "something broke on our end" - it gets the same
  // 503 shape as the offline and stalled errors, which keeps the Refresh button.
  return createError({
    statusCode: 503,
    statusMessage: 'Something unexpected went wrong. Refresh to try again.',
    data: { title: "Couldn't load this page" },
  });
}

// A wait for store data that ran out while the tab was in the foreground the whole time. The
// data is not coming, but nothing on the server failed, so this must not read as a 500: it gets
// the same 503 shape as the offline error, which keeps the Refresh button and blames the
// connection rather than us. See useAwaitStoreData for why a timeout across a hidden tab is
// retried instead of ending up here.
export function createStalledDataError() {
  return createError({
    statusCode: 503,
    statusMessage: 'This is taking longer than usual. Check your connection and try again.',
    data: { title: "Couldn't load this page" },
  });
}
